/**
 * AppHub — Hub (Superadmin + Admin) root component.
 *
 * This is the full application including superadmin routes.
 * Used by main-hub.tsx entry point for Hub deployments.
 *
 * NOTE: If you add routes to MainAppContent, also add them in AppSpoke.tsx.
 */
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuth } from './contexts/AuthContext'
import { useIndustry } from './contexts/IndustryContext'
import MainLayout from './components/layout/MainLayout'
import Login from './modules/auth/Login'
import { Loader2 } from 'lucide-react'

// ─── Route-level code splitting ────────────────────────────────────────
// Every page-sized component is `lazy()` so its JS only downloads when
// the matching route is visited. The 5.2 MB main bundle was eagerly
// pulling in 30 Brain pages + 17 industry templates + admin + superadmin
// + plotly even when the user only opened /dashboard. Suspense fallback
// (below in MainAppContent) renders a small spinner while a chunk is
// in-flight.
//
// Imports kept eager:
//   - MainLayout, Login, Loader2 — first paint
//   - Route-guard helpers (IndustryRoute, HubOnlyRoute) — synchronous
//   - Hooks (useAuth, useIndustry, useDeploymentMode, useInvestigationSync)
//   - SuperadminAuthProvider — context that wraps the lazy superadmin
//     pages; if this were lazy too we'd hit the wrapping Suspense
//     boundary every superadmin nav.

// Core modules
const CommandCenter = lazy(() => import('./modules/command-center/CommandCenter'))
const AssetIntelligence = lazy(() => import('./modules/asset-intelligence/AssetIntelligenceRouter'))
const PatientIntelligence = lazy(() => import('./modules/patient-intelligence/PatientIntelligence'))
const PredictiveOps = lazy(() => import('./modules/predictive-ops/PredictiveOps'))
const ComparativeAnalytics = lazy(() => import('./modules/comparative-analytics/ComparativeAnalytics'))
const RootCauseExplorer = lazy(() => import('./modules/root-cause/RootCauseExplorer'))
const MaintenanceIntelligence = lazy(() => import('./modules/maintenance/MaintenanceRouter'))
const SelfLearning = lazy(() => import('./modules/self-learning/SelfLearningRouter'))
const FederatedLearning = lazy(() => import('./modules/federated-learning/FederatedLearning'))
const MinePlanMap = lazy(() => import('./modules/mine-plan/MinePlanMap'))
// Signature Pages — registry-driven sub-vertical family (1 of 6 primitives each)
const SignatureFamilyPage = lazy(() => import('./modules/signature/SignatureFamilyPage'))
const SignaturePageRenderer = lazy(() => import('./modules/signature/SignaturePageRenderer'))
const ExecutiveInsights = lazy(() => import('./modules/executive/ExecutiveInsights'))
const Settings = lazy(() => import('./modules/settings/Settings'))
const UserNotifications = lazy(() => import('./modules/notifications/UserNotifications'))
const HelpCenter = lazy(() => import('./modules/help/HelpCenter'))

// Brain Intelligence sub-nav pages (30) — each lazy so plotly-using
// pages (FailurePredictionTimeline, SHAP, etc.) only download their
// chart deps when that specific page mounts.
const BrainDashboardPage = lazy(() => import('./modules/self-learning/pages/BrainDashboardPage'))
const PredictionAuditPage = lazy(() => import('./modules/self-learning/pages/PredictionAuditPage'))
const ReasoningVisualizerPage = lazy(() => import('./modules/self-learning/pages/ReasoningVisualizerPage'))
const KnowledgeBrowserPage = lazy(() => import('./modules/self-learning/pages/KnowledgeBrowserPage'))
const CausalGraphPage = lazy(() => import('./modules/self-learning/pages/CausalGraphPage'))
const SelfHealingPage = lazy(() => import('./modules/self-learning/pages/SelfHealingPage'))
const MemoryBrowserPage = lazy(() => import('./modules/self-learning/pages/MemoryBrowserPage'))
const FairnessDashboardPage = lazy(() => import('./modules/self-learning/pages/FairnessDashboardPage'))
const GenerativeDesignPage = lazy(() => import('./modules/self-learning/pages/GenerativeDesignPage'))
const AGIChatPage = lazy(() => import('./modules/self-learning/pages/AGIChatPage'))
const OracleConsolePage = lazy(() => import('./modules/self-learning/pages/OracleConsolePage'))
const DigitalTwinPage = lazy(() => import('./modules/self-learning/pages/DigitalTwinPage'))
// Phase 8E Day 1 — 3D scene viewer (Embedded3DContext + IsometricViewer)
const DigitalTwin3DPage = lazy(() => import('./modules/digital-twin/DigitalTwin3DPage'))
// Sprint 3 Day 2 — Financial sister product drill-in
const FinancialCaseDetailPage = lazy(() => import('./modules/financial/FinancialCaseDetailPage'))
// Sprint 3 Day 5 — Transaction drill-in
const FinancialTransactionDetailPage = lazy(() => import('./modules/financial/FinancialTransactionDetailPage'))
// Sprint 4 Day 2 — Phase 8F drill-in pages
const WindTurbineDetailPage = lazy(() => import('./modules/wind/WindTurbineDetailPage'))
const WindFaultCategoryPage = lazy(() => import('./modules/wind/WindFaultCategoryPage'))
const DefenceProgramDetailPage = lazy(() => import('./modules/defence/DefenceProgramDetailPage'))
const DefenceCMMCAssetDetailPage = lazy(() => import('./modules/defence/DefenceCMMCAssetDetailPage'))
const NuclearReactorDetailPage = lazy(() => import('./modules/nuclear/NuclearReactorDetailPage'))
const NuclearROPCornerstonePage = lazy(() => import('./modules/nuclear/NuclearROPCornerstonePage'))
const GoalPlanningPage = lazy(() => import('./modules/self-learning/pages/GoalPlanningPage'))
const PredictionObservatoryPage = lazy(() => import('./modules/self-learning/pages/PredictionObservatoryPage'))
const ExplainabilityWorkbenchPage = lazy(() => import('./modules/self-learning/pages/ExplainabilityWorkbenchPage'))
const PhysicsLabPage = lazy(() => import('./modules/self-learning/pages/PhysicsLabPage'))
const ModelHealthCommandPage = lazy(() => import('./modules/self-learning/pages/ModelHealthCommandPage'))
const KnowledgeWorkbenchPage = lazy(() => import('./modules/self-learning/pages/KnowledgeWorkbenchPage'))
// 2026-07-03 — these 6 dedicated pages existed but were swallowed by the
// /learning/* catch-all (all rendered the SelfLearning landing → 6 identical
// sidebar sub-navs). Wire them to their real routes.
const LoRAAdapterPage = lazy(() => import('./modules/self-learning/pages/LoRAAdapterPage'))
const AdapterSyncPage = lazy(() => import('./modules/self-learning/pages/AdapterSyncPage'))
const ContinualLearningPage = lazy(() => import('./modules/self-learning/pages/ContinualLearningPage'))
const LearningCyclesPage = lazy(() => import('./modules/self-learning/pages/LearningCyclesPage'))
const DatasetDiscoveryPage = lazy(() => import('./modules/self-learning/pages/DatasetDiscoveryPage'))
const KnowledgeUpdateStatusPage = lazy(() => import('./modules/self-learning/pages/KnowledgeUpdateStatusPage'))
const DesignStudioPage = lazy(() => import('./modules/self-learning/pages/DesignStudioPage'))
const NarrativeHubPage = lazy(() => import('./modules/self-learning/pages/NarrativeHubPage'))
const CognitiveTwinPage = lazy(() => import('./modules/self-learning/pages/CognitiveTwinPage'))
const ModelAccuracyPage = lazy(() => import('./modules/self-learning/pages/ModelAccuracyPage'))
const ARVideoAnalysisPage = lazy(() => import('./modules/self-learning/pages/ARVideoAnalysisPage'))
const KnowledgeGraphExplorerPage = lazy(() => import('./modules/self-learning/pages/KnowledgeGraphExplorerPage'))
const NeuroSymbolicPage = lazy(() => import('./modules/self-learning/pages/NeuroSymbolicPage'))
const DecisionOptimizerPage = lazy(() => import('./modules/self-learning/pages/DecisionOptimizerPage'))
const HypothesisLabPage = lazy(() => import('./modules/self-learning/pages/HypothesisLabPage'))
const ExperimentLabPage = lazy(() => import('./modules/self-learning/pages/ExperimentLabPage'))
const SelfEvolutionPage = lazy(() => import('./modules/self-learning/pages/SelfEvolutionPage'))
const DreamingMonitorPage = lazy(() => import('./modules/self-learning/pages/DreamingMonitorPage'))
const BrainSecurityPage = lazy(() => import('./modules/self-learning/pages/BrainSecurityPage'))
const MetaCognitionPage = lazy(() => import('./modules/self-learning/pages/MetaCognitionPage'))
const FederatedLearningPage = lazy(() => import('./modules/self-learning/pages/FederatedLearningPage'))
const AgentTeamsPage = lazy(() => import('./modules/self-learning/pages/AgentTeamsPage'))
const MetaOrchestraPage = lazy(() => import('./modules/self-learning/pages/MetaOrchestraPage'))
const CollaborativeArtifactsPage = lazy(() => import('./modules/self-learning/pages/CollaborativeArtifactsPage'))
const IndustryDiscoveryPage = lazy(() => import('./modules/self-learning/pages/IndustryDiscoveryPage'))
const BrainKPIDashboardPage = lazy(() => import('./modules/self-learning/pages/BrainKPIDashboardPage'))
// SOTA Substrate Dashboard — P4.1 (2026-06-21)
const SotaDashboardPage = lazy(() => import('./pages/sota/SotaDashboardPage'))

// Arc / Playground pages — Hub-native (superadmin always has full access;
// tier-gating only applies on Spoke deployments via HubOnlyPage)
const ArcConsolePage = lazy(() => import('./modules/arc/ArcConsolePage'))
const ArcObjectivesPage = lazy(() => import('./modules/arc/ArcObjectivesPage'))
const ArcDesignStudioPage = lazy(() => import('./modules/arc/ArcDesignStudioPage'))
const ArcExperimentsPage = lazy(() => import('./modules/arc/ArcExperimentsPage'))
const ArcApprovalsPage = lazy(() => import('./modules/arc/ArcApprovalsPage'))
const ArcLearningPage = lazy(() => import('./modules/arc/ArcLearningPage'))

// Phase D.5 — merged tab wrappers
const BrainExplainTabs = lazy(() => import('./components/brain-merged/BrainExplainTabs'))
const BrainModelHealthTabs = lazy(() => import('./components/brain-merged/BrainModelHealthTabs'))
const BrainEvolutionStrip = lazy(() => import('./components/brain-merged/BrainEvolutionStrip'))
const BrainKnowledgeBrowser = lazy(() => import('./components/brain-merged/BrainKnowledgeBrowser'))
const InvestigationNotebookPage = lazy(() => import('./pages/brain/InvestigationNotebookPage'))

import useInvestigationSync from './hooks/useInvestigationSync'

// Module config for industry-aware routing
import { isModuleEnabled } from './config/modules'
import { useDeploymentMode } from './hooks/useDeploymentMode'

// Admin modules — lazy. The admin section is a sibling of the main
// product; users typically don't touch it during normal use.
const AdminOverview = lazy(() => import('./modules/admin/AdminOverview'))
const UserManagement = lazy(() => import('./modules/admin/UserManagement'))
const UserForm = lazy(() => import('./modules/admin/UserForm'))
// Week 4 — KPI Studio admin (list / create / edit)
const KpiStudio = lazy(() => import('./modules/admin/KpiStudio'))
const RoleManagement = lazy(() => import('./modules/admin/RoleManagement'))
const NotificationSettings = lazy(() => import('./modules/admin/notifications/NotificationSettings'))
const ConnectorManagement = lazy(() => import('./modules/admin/connectors/ConnectorManagement'))
const SystemSettings = lazy(() => import('./modules/admin/settings/SystemSettings'))
const ApiUsageTracking = lazy(() => import('./modules/admin/settings/ApiUsageTracking'))
const AuditLog = lazy(() => import('./modules/admin/settings/AuditLog'))
// F1-F4 multi-tenant customization stack (2026-06-26)
const OrgConfigOverlay = lazy(() => import('./modules/admin/OrgConfigOverlay'))
const OrgCompare = lazy(() => import('./modules/admin/OrgCompare'))
// W4 Hub Admin Console (2026-06-27): per-customer fleet view, license re-issue,
// dynamic card registry + AI-proposed cards, programmatic Spoke bundle build.
const CustomersConsole = lazy(() => import('./modules/admin/CustomersConsole'))
const LicenseManager = lazy(() => import('./modules/admin/LicenseManager'))
const CardsManager = lazy(() => import('./modules/admin/CardsManager'))

// Phase-2 workflow admin pages.
const FeedbackDashboardPage = lazy(() => import('./pages/admin/FeedbackDashboardPage'))
const JourneyReplayPage = lazy(() => import('./pages/admin/JourneyReplayPage'))
const AgentApprovalsPage = lazy(() => import('./pages/admin/AgentApprovalsPage'))
const LearningRunsPage = lazy(() => import('./pages/admin/LearningRunsPage'))
const KbCoverageHeatmapPage = lazy(() => import('./pages/admin/KbCoverageHeatmapPage'))
const CapabilitiesPage = lazy(() => import('./pages/about/CapabilitiesPage'))

// Superadmin modules — Hub-only. Auth provider stays eager (it wraps
// the Suspense boundary inside SuperadminRoutes); pages are lazy.
import { SuperadminAuthProvider } from './modules/superadmin/SuperadminAuthContext'
const SuperadminLogin = lazy(() => import('./modules/superadmin/SuperadminLogin'))
const SuperadminDashboard = lazy(() => import('./modules/superadmin/SuperadminDashboard'))
const SuperadminOverview = lazy(() => import('./modules/superadmin/SuperadminOverview'))
const LicenseManagement = lazy(() => import('./modules/superadmin/LicenseManagement'))
const DemoLicenseManager = lazy(() => import('./modules/superadmin/DemoLicenseManager'))
const SubscriptionManagement = lazy(() => import('./modules/superadmin/SubscriptionManagement'))
const OrganizationManagement = lazy(() => import('./modules/superadmin/OrganizationManagement'))
const SuperadminAuditLog = lazy(() => import('./modules/superadmin/AuditLog'))
const SuperadminSettings = lazy(() => import('./modules/superadmin/SuperadminSettings'))
const SpokeManagement = lazy(() => import('./modules/superadmin/SpokeManagement'))
const FleetSync = lazy(() => import('./modules/superadmin/FleetSync'))
const FleetTelemetry = lazy(() => import('./modules/superadmin/FleetTelemetry'))
const AutoOnboard = lazy(() => import('./modules/superadmin/AutoOnboard'))
const TrainingMonitoring = lazy(() => import('./modules/superadmin/TrainingMonitoring'))
const BackgroundTaskMonitor = lazy(() => import('./modules/superadmin/BackgroundTaskMonitor'))
const Datasets = lazy(() => import('./modules/superadmin/Datasets'))
const IndustrialConnectors = lazy(() => import('./modules/superadmin/IndustrialConnectors'))
const ReportLibrary = lazy(() => import('./modules/superadmin/ReportLibrary'))
const BrainIntelligenceMonitor = lazy(() => import('./modules/superadmin/BrainIntelligenceMonitor'))

// Phase 8f-8p — 2026-06-25 panels (lazy-loaded; under /phase8/* route prefix)
const Phase8IndustryLandingPage = lazy(() => import('./components/phase8h/IndustryLandingPage').then(m => ({ default: m.IndustryLandingPage })))
const Phase8AssetCatalog = lazy(() => import('./components/phase8j/AssetCatalog').then(m => ({ default: m.AssetCatalog })))
const Phase8WOWFeaturePanel = lazy(() => import('./components/phase8k/WOWFeaturePanel').then(m => ({ default: m.WOWFeaturePanel })))
const Phase8CognitivePanel = lazy(() => import('./components/phase8l/CognitivePanel').then(m => ({ default: m.CognitivePanel })))
const Phase8DeepAnalyzerPanel = lazy(() => import('./components/phase8m/DeepAnalyzerPanel').then(m => ({ default: m.DeepAnalyzerPanel })))
const Phase8MixedRealityPanel = lazy(() => import('./components/phase8n/MixedRealityPanel').then(m => ({ default: m.MixedRealityPanel })))
const Phase8MetaLearningPanel = lazy(() => import('./components/phase8o/MetaLearningPanel').then(m => ({ default: m.MetaLearningPanel })))
const Phase8TrustPanel = lazy(() => import('./components/phase8p/TrustPanel').then(m => ({ default: m.TrustPanel })))
const Phase8WebAuthnLogin = lazy(() => import('./components/phase8i/WebAuthnLogin').then(m => ({ default: m.WebAuthnLogin })))
const Phase8ThemeSwitcher = lazy(() => import('./components/phase8i/ThemeSwitcher').then(m => ({ default: m.ThemeSwitcher })))

// Suspense fallback shown while a route chunk is downloading. Compact
// so it doesn't flash unnecessarily on fast networks.
function RouteChunkFallback() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
    </div>
  )
}

// Placeholder component for future pages
function PlaceholderPage({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <h2 className="text-xl font-semibold text-surface-100 mb-2">{title}</h2>
      <p className="text-surface-400">{description}</p>
      <p className="mt-4 text-sm text-surface-500">Coming soon...</p>
    </div>
  )
}

// Component for disabled modules (industry-specific)
function ModuleDisabled({ moduleName }: { moduleName: string }) {
  const { industry } = useIndustry()
  return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <div className="w-16 h-16 rounded-full bg-surface-800 flex items-center justify-center mb-4">
        <Loader2 className="w-8 h-8 text-surface-600" />
      </div>
      <h2 className="text-xl font-semibold text-surface-100 mb-2">{moduleName} Not Available</h2>
      <p className="text-surface-400 max-w-md">
        This module is not applicable for the {industry.name} industry.
      </p>
      <p className="mt-4 text-sm text-surface-500">
        Select a different industry or contact your administrator.
      </p>
    </div>
  )
}

// Industry-aware route guard
function IndustryRoute({
  moduleId,
  element,
  moduleName,
}: {
  moduleId: string
  element: React.ReactNode
  moduleName: string
}) {
  const { industryId } = useIndustry()
  const enabled = isModuleEnabled(moduleId as any, industryId)

  if (!enabled) {
    return <ModuleDisabled moduleName={moduleName} />
  }

  return <>{element}</>
}

// Hub-only route guard — redirects to Command Center on Spoke/Client
function HubOnlyRoute({
  moduleId,
  element,
  moduleName,
}: {
  moduleId: string
  element: React.ReactNode
  moduleName: string
}) {
  const { isHub, loading } = useDeploymentMode()

  // Wait for /health/deployment-mode to resolve before deciding. The hook
  // defaults is_hub=false to avoid flashing Hub features on Spoke; redirecting
  // on that default raced direct navigations (e.g. /brain/design-studio) to
  // Command Center on the live Hub. Only redirect once we KNOW it's not a Hub.
  if (loading) {
    return null
  }

  if (!isHub) {
    return <Navigate to="/" replace />
  }

  return (
    <IndustryRoute moduleId={moduleId} element={element} moduleName={moduleName} />
  )
}

// 2026-07-03 — Industry Overview (Phase 8h landing) scoped to the CURRENT
// industry (was hardcoded oil_gas). Reads useIndustry so wearable_etd etc.
// show their own config, not oil_gas.
function CurrentIndustryLanding() {
  const { industryId } = useIndustry()
  return <Phase8IndustryLandingPage industry={industryId} />
}

// Healthcare-aware Asset Intelligence switcher
function AssetOrPatientIntelligence() {
  const { industryId } = useIndustry()

  // Use Patient Intelligence for healthcare, Asset Intelligence for all others
  if (industryId === 'healthcare') {
    return <PatientIntelligence />
  }

  return <AssetIntelligence />
}

// Superadmin Routes Component - Completely separate from main app auth
// On Spoke/Client deployments, redirect to main app (superadmin is Hub-only)
function SuperadminRoutes() {
  const { isHub, loading } = useDeploymentMode()

  // Wait for deployment mode detection before making routing decisions
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-900">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-surface-400 text-sm">Connecting to Hub...</p>
        </div>
      </div>
    )
  }

  if (!isHub) {
    return (
      <Routes>
        <Route path="/superadmin/*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  return (
    <SuperadminAuthProvider>
      <Suspense fallback={<RouteChunkFallback />}>
        <Routes>
          <Route path="/superadmin/login" element={<SuperadminLogin />} />
          <Route path="/superadmin" element={<SuperadminDashboard />}>
            <Route index element={<SuperadminOverview />} />
            <Route path="organizations" element={<OrganizationManagement />} />
            <Route path="licenses" element={<LicenseManagement />} />
            <Route path="demo-licenses" element={<DemoLicenseManager />} />
            <Route path="subscriptions" element={<SubscriptionManagement />} />
            <Route path="spokes" element={<SpokeManagement />} />
            <Route path="fleet" element={<FleetSync />} />
            <Route path="telemetry" element={<FleetTelemetry />} />
            <Route path="auto-onboard" element={<AutoOnboard />} />
            <Route path="datasets" element={<Datasets />} />
            <Route path="industrial-connectors" element={<IndustrialConnectors />} />
            <Route path="report-library" element={<ReportLibrary />} />
            <Route path="training" element={<TrainingMonitoring />} />
            <Route path="brain" element={<BrainIntelligenceMonitor />} />
            <Route path="tasks" element={<BackgroundTaskMonitor />} />
            <Route path="audit" element={<SuperadminAuditLog />} />
            <Route path="settings" element={<SuperadminSettings />} />
          </Route>
          {/* Redirect unknown superadmin routes to dashboard */}
          <Route path="/superadmin/*" element={<Navigate to="/superadmin" replace />} />
        </Routes>
      </Suspense>
    </SuperadminAuthProvider>
  )
}

// Main App content that requires main auth context
function MainAppContent() {
  const { isAuthenticated, isLoading } = useAuth()
  // Persist active investigation thread to the backend (debounced).
  useInvestigationSync()

  // Show loading state while auth is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          <p className="text-surface-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />
  }

  return (
    <MainLayout>
      <Suspense fallback={<RouteChunkFallback />}>
      <Routes>
        {/* Command Center - Main dashboard with sub-pages */}
        <Route
          path="/"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Command Center"
              element={<CommandCenter />}
            />
          }
        />
        <Route
          path="/alerts"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Command Center"
              element={<CommandCenter />}
            />
          }
        />
        <Route
          path="/intelligence"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Command Center"
              element={<CommandCenter />}
            />
          }
        />
        <Route
          path="/actions"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Command Center"
              element={<CommandCenter />}
            />
          }
        />

        {/* Asset Intelligence — inner router handles index, sub-pages, and :assetId
            (Patient Intelligence for healthcare industries). */}
        <Route
          path="/assets"
          element={
            <IndustryRoute
              moduleId="asset-intelligence"
              moduleName="Asset Intelligence"
              element={<AssetOrPatientIntelligence />}
            />
          }
        />
        <Route
          path="/assets/*"
          element={
            <IndustryRoute
              moduleId="asset-intelligence"
              moduleName="Asset Intelligence"
              element={<AssetOrPatientIntelligence />}
            />
          }
        />
        {/* 2026-06-09: drill-down route. `/asset-intel/*` avoids the
            FastAPI StaticFiles mount at `/assets` so direct loads + page
            reloads work (SPA fallback returns index.html). Used by
            useAssetDrillDown() from Command Center widget row clicks, and
            (2026-07-02) by the sidebar sub-nav (list/overview/health/...)
            since /assets/<sub> would 404 on hard refresh. */}
        <Route
          path="/asset-intel"
          element={
            <IndustryRoute
              moduleId="asset-intelligence"
              moduleName="Asset Intelligence"
              element={<AssetOrPatientIntelligence />}
            />
          }
        />
        <Route
          path="/asset-intel/*"
          element={
            <IndustryRoute
              moduleId="asset-intelligence"
              moduleName="Asset Intelligence"
              element={<AssetOrPatientIntelligence />}
            />
          }
        />

        {/* Mine Plan Map - mining industry only */}
        <Route
          path="/mine-plan"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Mine Plan Map"
              element={<MinePlanMap />}
            />
          }
        />

        {/* Signature Pages — registry-driven sub-vertical family. Cross-industry
            (not module-gated): family landing + per-sub-vertical resolved page,
            each drawn by one of 6 primitives via SignaturePageRenderer. */}
        <Route path="/signature" element={<SignatureFamilyPage />} />
        <Route path="/signature/:industry" element={<SignatureFamilyPage />} />
        <Route
          path="/signature/:industry/:subVertical"
          element={<SignaturePageRenderer />}
        />

        {/* Predictive Ops - with sub-pages */}
        <Route
          path="/predictions"
          element={
            <IndustryRoute
              moduleId="predictive-ops"
              moduleName="Predictive Operations"
              element={<PredictiveOps />}
            />
          }
        />

        {/* Multi-Head Prediction Observatory */}
        <Route
          path="/predictions/observatory"
          element={
            <IndustryRoute
              moduleId="predictive-ops"
              moduleName="Prediction Observatory"
              element={<PredictionObservatoryPage />}
            />
          }
        />

        {/* Interactive Explainability Workbench */}
        <Route
          path="/predictions/explain"
          element={
            <IndustryRoute
              moduleId="predictive-ops"
              moduleName="Explainability Workbench"
              element={<ExplainabilityWorkbenchPage />}
            />
          }
        />

        <Route
          path="/predictions/*"
          element={
            <IndustryRoute
              moduleId="predictive-ops"
              moduleName="Predictive Operations"
              element={<PredictiveOps />}
            />
          }
        />

        {/* Comparative Analytics - with sub-pages */}
        <Route
          path="/compare"
          element={
            <IndustryRoute
              moduleId="compare"
              moduleName="Comparative Analytics"
              element={<ComparativeAnalytics />}
            />
          }
        />
        <Route
          path="/compare/*"
          element={
            <IndustryRoute
              moduleId="compare"
              moduleName="Comparative Analytics"
              element={<ComparativeAnalytics />}
            />
          }
        />

        {/* Root Cause Explorer — inner router handles index, static sub-pages, and :assetId */}
        <Route
          path="/root-cause"
          element={
            <IndustryRoute
              moduleId="root-cause"
              moduleName="Root Cause Analysis"
              element={<RootCauseExplorer />}
            />
          }
        />
        <Route
          path="/root-cause/*"
          element={
            <IndustryRoute
              moduleId="root-cause"
              moduleName="Root Cause Analysis"
              element={<RootCauseExplorer />}
            />
          }
        />

        {/* Maintenance Intelligence - with sub-pages (disabled for financial) */}
        <Route
          path="/maintenance"
          element={
            <IndustryRoute
              moduleId="maintenance"
              moduleName="Maintenance Intelligence"
              element={<MaintenanceIntelligence />}
            />
          }
        />
        <Route
          path="/maintenance/*"
          element={
            <IndustryRoute
              moduleId="maintenance"
              moduleName="Maintenance Intelligence"
              element={<MaintenanceIntelligence />}
            />
          }
        />

        {/* Self-Learning Engine - with sub-pages */}
        <Route
          path="/learning"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Self-Learning Engine"
              element={<SelfLearning />}
            />
          }
        />

        {/* SME Knowledge & Feedback Workbench */}
        <Route
          path="/learning/knowledge-workbench"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Knowledge Workbench"
              element={<KnowledgeWorkbenchPage />}
            />
          }
        />

        {/* 2026-07-03 — dedicated Self-Learning sub-pages (were rendering the
            landing page via the catch-all → 6 identical sidebar entries). */}
        <Route path="/learning/lora-adapters" element={<IndustryRoute moduleId="self-learning" moduleName="LoRA Adapters" element={<LoRAAdapterPage />} />} />
        <Route path="/learning/adapter-sync" element={<IndustryRoute moduleId="self-learning" moduleName="Adapter Sync" element={<AdapterSyncPage />} />} />
        <Route path="/learning/continual" element={<IndustryRoute moduleId="self-learning" moduleName="Continual Learning" element={<ContinualLearningPage />} />} />
        <Route path="/learning/learning-cycles" element={<IndustryRoute moduleId="self-learning" moduleName="Learning Cycles" element={<LearningCyclesPage />} />} />
        <Route path="/learning/dataset-discovery" element={<IndustryRoute moduleId="self-learning" moduleName="Dataset Discovery" element={<DatasetDiscoveryPage />} />} />
        <Route path="/learning/knowledge-updates" element={<IndustryRoute moduleId="self-learning" moduleName="Knowledge Updates" element={<KnowledgeUpdateStatusPage />} />} />

        <Route
          path="/learning/*"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Self-Learning Engine"
              element={<SelfLearning />}
            />
          }
        />

        {/* Federated Learning - Hub only (distributed training coordination) */}
        <Route
          path="/federated"
          element={
            <HubOnlyRoute
              moduleId="self-learning"
              moduleName="Federated Learning"
              element={<FederatedLearning />}
            />
          }
        />
        <Route
          path="/federated/*"
          element={
            <HubOnlyRoute
              moduleId="self-learning"
              moduleName="Federated Learning"
              element={<FederatedLearning />}
            />
          }
        />

        {/* Brain Intelligence Dashboard - runs locally (L1-L4) */}
        <Route
          path="/brain"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Brain Intelligence"
              element={<BrainDashboardPage />}
            />
          }
        />

        {/* Prediction Audit Trail - local predictions */}
        <Route
          path="/brain/audit"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Prediction Audit"
              element={<PredictionAuditPage />}
            />
          }
        />

        {/* Reasoning Chain Visualizer - L4 runs locally */}
        <Route
          path="/brain/reasoning"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Reasoning Visualizer"
              element={<ReasoningVisualizerPage />}
            />
          }
        />

        {/* Knowledge Base Browser - L3 SOP/RAG runs locally */}
        <Route
          path="/brain/knowledge"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Knowledge Base"
              element={<KnowledgeBrowserPage />}
            />
          }
        />

        {/* Causal Graph Viewer - L4 causal runs locally */}
        <Route
          path="/brain/causal"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Causal Graph"
              element={<CausalGraphPage />}
            />
          }
        />

        {/* Self-Healing Dashboard - Hub only */}
        <Route
          path="/brain/healing"
          element={
            <HubOnlyRoute
              moduleId="self-learning"
              moduleName="Self-Healing"
              element={<SelfHealingPage />}
            />
          }
        />

        {/* Memory & EWC Browser - Hub only */}
        <Route
          path="/brain/memory"
          element={
            <HubOnlyRoute
              moduleId="self-learning"
              moduleName="Memory Browser"
              element={<MemoryBrowserPage />}
            />
          }
        />

        {/* AI Fairness Dashboard - runs locally (L2 bias detection) */}
        <Route
          path="/brain/fairness"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="AI Fairness"
              element={<FairnessDashboardPage />}
            />
          }
        />

        {/* Generative Design Studio - Hub only (L5 generative) */}
        <Route
          path="/brain/design"
          element={
            <HubOnlyRoute
              moduleId="self-learning"
              moduleName="Generative Design"
              element={<GenerativeDesignPage />}
            />
          }
        />

        {/* AI Assistant Chat - runs locally (LLM integration) */}
        <Route
          path="/brain/chat"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="AI Assistant"
              element={<AGIChatPage />}
            />
          }
        />

        {/* RYEDORE Oracle — Unified Conversational Intelligence */}
        <Route
          path="/oracle"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="RYEDORE Oracle"
              element={<OracleConsolePage />}
            />
          }
        />

        {/* Digital Twin Simulator - runs locally (L4 simulation) */}
        <Route
          path="/brain/twin"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Digital Twin"
              element={<DigitalTwinPage />}
            />
          }
        />

        {/* Phase 8E Day 1 — Digital Twin 3D scene viewer */}
        <Route
          path="/twin/3d"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Digital Twin 3D"
              element={<DigitalTwin3DPage />}
            />
          }
        />

        {/* Sprint 3 Day 2 — Financial sister product drill-in */}
        <Route
          path="/financial/cases/:caseId"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Financial Case Detail"
              element={<FinancialCaseDetailPage />}
            />
          }
        />

        {/* Sprint 3 Day 5 — Transaction drill-in */}
        <Route
          path="/financial/transactions/:txnId"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Financial Transaction Detail"
              element={<FinancialTransactionDetailPage />}
            />
          }
        />

        {/* Sprint 4 Day 2 — Phase 8F drill-in pages */}
        <Route
          path="/wind/turbines/:turbineId"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Wind Turbine Detail"
              element={<WindTurbineDetailPage />}
            />
          }
        />
        <Route
          path="/wind/faults"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Wind Fault Categories"
              element={<WindFaultCategoryPage />}
            />
          }
        />
        <Route
          path="/defence/programs/:programId"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Defence Program Detail"
              element={<DefenceProgramDetailPage />}
            />
          }
        />
        <Route
          path="/defence/cmmc/:assetId"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Defence CMMC Asset Detail"
              element={<DefenceCMMCAssetDetailPage />}
            />
          }
        />
        <Route
          path="/nuclear/reactors/:reactorId"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Nuclear Reactor Detail"
              element={<NuclearReactorDetailPage />}
            />
          }
        />
        <Route
          path="/nuclear/cornerstones"
          element={
            <IndustryRoute
              moduleId="command-center"
              moduleName="Nuclear ROP Cornerstones"
              element={<NuclearROPCornerstonePage />}
            />
          }
        />

        {/* Goal Planning - Hub only (L5 RL optimization) */}
        <Route
          path="/brain/goals"
          element={
            <HubOnlyRoute
              moduleId="self-learning"
              moduleName="Goal Planning"
              element={<GoalPlanningPage />}
            />
          }
        />

        {/* Physics Simulation Laboratory */}
        <Route
          path="/brain/physics"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Physics Lab"
              element={<PhysicsLabPage />}
            />
          }
        />

        {/* Model Health & Retraining Command Center */}
        <Route
          path="/brain/model-health"
          element={
            <IndustryRoute
              moduleId="self-learning"
              moduleName="Model Health"
              element={<ModelHealthCommandPage />}
            />
          }
        />

        {/* Generative Design & Optimization Studio - Hub only */}
        <Route
          path="/brain/design-studio"
          element={
            <HubOnlyRoute
              moduleId="self-learning"
              moduleName="Design Studio"
              element={<DesignStudioPage />}
            />
          }
        />

        {/* Executive Insights - with sub-pages */}
        <Route
          path="/executive"
          element={
            <IndustryRoute
              moduleId="executive"
              moduleName="Executive Insights"
              element={<ExecutiveInsights />}
            />
          }
        />

        {/* Narrative Intelligence & Reporting Hub */}
        <Route
          path="/executive/narratives"
          element={
            <IndustryRoute
              moduleId="executive"
              moduleName="Narrative Hub"
              element={<NarrativeHubPage />}
            />
          }
        />

        <Route
          path="/executive/*"
          element={
            <IndustryRoute
              moduleId="executive"
              moduleName="Executive Insights"
              element={<ExecutiveInsights />}
            />
          }
        />

        {/* User-specific routes */}
        <Route path="/notifications" element={<UserNotifications />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/help" element={<HelpCenter />} />

        {/* Why Ryedore — buyer-facing capabilities surface (Track C, Apr-28) */}
        <Route path="/about/capabilities" element={<CapabilitiesPage />} />

        {/* Admin Routes - Now using same MainLayout with unified sidebar */}
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/users" element={<UserManagement />} />
        <Route path="/admin/users/new" element={<UserForm />} />
        <Route path="/admin/users/:id/edit" element={<UserForm />} />
        {/* Week 4 — KPI Studio admin */}
        <Route path="/admin/kpis" element={<KpiStudio mode="list" />} />
        <Route path="/admin/kpis/new" element={<KpiStudio mode="new" />} />
        <Route path="/admin/kpis/:id" element={<KpiStudio mode="edit" />} />
        <Route path="/admin/roles" element={<RoleManagement />} />
        <Route path="/admin/permissions" element={<RoleManagement />} />
        <Route path="/admin/notifications" element={<NotificationSettings />} />
        <Route path="/admin/connectors" element={<ConnectorManagement />} />
        <Route
          path="/admin/usage"
          element={
            <HubOnlyRoute
              moduleId="command-center"
              moduleName="API Usage Tracking"
              element={<ApiUsageTracking />}
            />
          }
        />
        <Route path="/admin/settings" element={<SystemSettings />} />
        <Route
          path="/admin/audit"
          element={
            <HubOnlyRoute
              moduleId="command-center"
              moduleName="Audit Log"
              element={<AuditLog />}
            />
          }
        />

        {/* F1-F4 multi-tenant customization stack (2026-06-26) */}
        <Route path="/admin/orgs/overlay" element={<OrgConfigOverlay />} />
        <Route path="/admin/orgs/compare" element={<OrgCompare />} />

        {/* W4 Hub Admin Console (2026-06-27) — customers / licenses / cards */}
        <Route path="/admin/customers" element={<CustomersConsole />} />
        <Route path="/admin/license"   element={<LicenseManager />} />
        <Route path="/admin/cards"     element={<CardsManager />} />

        {/* Phase-2 workflow admin pages. */}
        <Route path="/admin/feedback" element={<FeedbackDashboardPage />} />
        <Route path="/admin/journey" element={<JourneyReplayPage />} />
        <Route path="/admin/agent-approvals" element={<AgentApprovalsPage />} />
        <Route path="/admin/learning-runs" element={<LearningRunsPage />} />
        <Route path="/admin/kb-coverage" element={<KbCoverageHeatmapPage />} />

        {/* Brain Intelligence — Product KPI Dashboard */}
        <Route path="/brain/kpis" element={<IndustryRoute moduleId="self-learning" moduleName="Brain KPIs" element={<BrainKPIDashboardPage />} />} />

        {/* Phase D.5 — merged tab wrapper routes (see AppSpoke.tsx for rationale). */}
        <Route path="/brain/explain" element={<IndustryRoute moduleId="self-learning" moduleName="Explain" element={<BrainExplainTabs />} />} />
        <Route path="/brain/model-health-tabs" element={<IndustryRoute moduleId="self-learning" moduleName="Model Health" element={<BrainModelHealthTabs />} />} />
        <Route path="/brain/evolution" element={<IndustryRoute moduleId="self-learning" moduleName="Evolution" element={<BrainEvolutionStrip />} />} />
        <Route path="/brain/knowledge-browser" element={<IndustryRoute moduleId="self-learning" moduleName="Knowledge Browser" element={<BrainKnowledgeBrowser />} />} />

        {/* Phase D.9 — Investigation Notebook (scientific-paper view of an active thread) */}
        <Route path="/brain/investigation" element={<IndustryRoute moduleId="self-learning" moduleName="Investigation Notebook" element={<InvestigationNotebookPage />} />} />
        <Route path="/brain/investigation/:id" element={<IndustryRoute moduleId="self-learning" moduleName="Investigation Notebook" element={<InvestigationNotebookPage />} />} />

        {/* ── BRAIN INTELLIGENCE: 17 sub-nav routes (parity with Spoke) ── */}
        {/* OBSERVE phase */}
        <Route path="/brain/cognitive-twin"     element={<IndustryRoute moduleId="self-learning" moduleName="Live Intelligence"     element={<CognitiveTwinPage />} />} />
        <Route path="/brain/model-quality"      element={<IndustryRoute moduleId="self-learning" moduleName="Prediction Accuracy"   element={<ModelAccuracyPage />} />} />
        <Route path="/brain/video-analysis"     element={<IndustryRoute moduleId="self-learning" moduleName="Visual Inspection"     element={<ARVideoAnalysisPage />} />} />
        {/* UNDERSTAND phase */}
        <Route path="/brain/knowledge-graph"    element={<IndustryRoute moduleId="self-learning" moduleName="Knowledge Graph"       element={<KnowledgeGraphExplorerPage />} />} />
        <Route path="/brain/neuro-symbolic"     element={<IndustryRoute moduleId="self-learning" moduleName="Neuro-Symbolic Engine" element={<NeuroSymbolicPage />} />} />
        {/* ACT phase */}
        <Route path="/brain/decision-optimizer" element={<IndustryRoute moduleId="self-learning" moduleName="Decision Optimizer"    element={<DecisionOptimizerPage />} />} />
        <Route path="/brain/hypothesis-lab"     element={<IndustryRoute moduleId="self-learning" moduleName="Hypothesis Lab"        element={<HypothesisLabPage />} />} />
        <Route path="/brain/experiment-lab"     element={<IndustryRoute moduleId="self-learning" moduleName="Experiment Lab"        element={<ExperimentLabPage />} />} />
        {/* EVOLVE phase */}
        <Route path="/brain/self-evolution"     element={<IndustryRoute moduleId="self-learning" moduleName="Self-Evolution"        element={<SelfEvolutionPage />} />} />
        <Route path="/brain/dreaming"           element={<IndustryRoute moduleId="self-learning" moduleName="Dreaming Monitor"      element={<DreamingMonitorPage />} />} />
        <Route path="/brain/security"           element={<IndustryRoute moduleId="self-learning" moduleName="Robustness Shield"     element={<BrainSecurityPage />} />} />
        <Route path="/brain/metacognition"      element={<IndustryRoute moduleId="self-learning" moduleName="Confidence Gate"       element={<MetaCognitionPage />} />} />
        <Route path="/brain/federated-learning" element={<IndustryRoute moduleId="self-learning" moduleName="Federated Learning"    element={<FederatedLearningPage />} />} />
        <Route path="/brain/meta-orchestra"     element={<IndustryRoute moduleId="self-learning" moduleName="Meta-Orchestra"        element={<MetaOrchestraPage />} />} />
        <Route path="/brain/artifacts"          element={<IndustryRoute moduleId="self-learning" moduleName="Collaborative Artifacts" element={<CollaborativeArtifactsPage />} />} />
        {/* Hub-only sub-pages */}
        <Route path="/brain/agent-teams"        element={<IndustryRoute moduleId="self-learning" moduleName="Agent Teams"           element={<AgentTeamsPage />} />} />
        <Route path="/brain/industry-discovery" element={<IndustryRoute moduleId="self-learning" moduleName="Industry Discovery"    element={<IndustryDiscoveryPage />} />} />

        {/* Arc — Agentic AI Planner. On Hub, superadmin has unconditional
            access (no tier gate, no HubOnlyPage wrapper — those exist only
            to handle Spoke license-gating). */}
        <Route path="/arc"             element={<ArcConsolePage />} />
        <Route path="/arc/objectives"  element={<ArcObjectivesPage />} />
        <Route path="/arc/design"      element={<ArcDesignStudioPage />} />
        <Route path="/arc/experiments" element={<ArcExperimentsPage />} />
        <Route path="/arc/approvals"   element={<ArcApprovalsPage />} />
        <Route path="/arc/learning"    element={<ArcLearningPage />} />

        {/* Legacy /playground/oracle → /arc redirect (Arc Console is the
            canonical AI chat surface on Hub). */}
        <Route path="/playground/oracle" element={<Navigate to="/arc" replace />} />
        <Route path="/playground"        element={<Navigate to="/arc" replace />} />

        {/* SOTA Substrate Dashboard — P4.1 (2026-06-21). Read-only operator
            diagnostic for module realness + tenant isolation + Merkle anchor. */}
        <Route path="/sota" element={<SotaDashboardPage />} />

        {/* Phase 8f-8p — 2026-06-25 new platform surfaces.
            2026-07-03 — was hardcoded industry="oil_gas" so Industry Overview
            showed oil_gas for every industry. Use the current industry. */}
        <Route path="/phase8/industry" element={<CurrentIndustryLanding />} />
        <Route path="/phase8/industry/:industry" element={<CurrentIndustryLanding />} />
        <Route path="/phase8/assets" element={<Phase8AssetCatalog />} />
        <Route path="/phase8/wow" element={<Phase8WOWFeaturePanel />} />
        <Route path="/phase8/cognitive" element={<Phase8CognitivePanel sessionId="session_default" />} />
        <Route path="/phase8/deep-analyzers" element={<Phase8DeepAnalyzerPanel />} />
        <Route path="/phase8/xr" element={<Phase8MixedRealityPanel />} />
        <Route path="/phase8/meta-learning" element={<Phase8MetaLearningPanel />} />
        <Route path="/phase8/trust" element={<Phase8TrustPanel />} />
        <Route path="/phase8/passkey/login" element={<Phase8WebAuthnLogin mode="login" userId="op_demo" />} />
        <Route path="/phase8/passkey/register" element={<Phase8WebAuthnLogin mode="register" userId="op_demo" userName="op_demo" />} />
        <Route path="/phase8/preferences" element={<Phase8ThemeSwitcher userId="op_demo" />} />

        {/* Catch-all redirect to Command Center */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </MainLayout>
  )
}

// Root Hub App Component - Routes between Superadmin and Main App
function AppHub() {
  const location = useLocation()

  // Check if we're on a superadmin route - these bypass normal auth completely
  if (location.pathname.startsWith('/superadmin')) {
    return <SuperadminRoutes />
  }

  // Render main app with its auth context
  return <MainAppContent />
}

export default AppHub
